import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCopy, FiDownload, FiMic, FiCheck } from 'react-icons/fi';
import ResultsTabs from '../components/ResultsTabs';
import { getHistoryItem } from '../utils/history';
import Title from '../assets/Title.png';
import './Results.css';

const Results = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTabContent, setActiveTabContent] = useState('brief');

  useEffect(() => {
    // Try to get data from navigation state first, then from history
    if (location.state?.transcript) {
      setData({
        transcript: location.state.transcript,
        brief: location.state.brief,
        detailed: location.state.detailed,
        detectedContext: location.state.detectedContext
      });
    } else if (id) {
      const historyItem = getHistoryItem(id);
      if (historyItem) {
        setData({
          transcript: historyItem.transcript,
          brief: historyItem.brief,
          detailed: historyItem.detailed,
          detectedContext: historyItem.detectedContext
        });
      } else {
        // Item not found, redirect to home
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [id, location.state, navigate]);

  const handleCopy = async () => {
    if (!data) return;

    const contentMap = {
      brief: data.brief,
      detailed: data.detailed,
      transcript: data.transcript
    };

    const content = contentMap[activeTabContent] || data.brief;

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadPDF = () => {
    if (!data) return;

    // Create a simple text version for download
    const content = `
UNSCRAMBLE - Structured Thoughts
================================

BRIEF VERSION:
${data.brief}

DETAILED VERSION:
${data.detailed}

ORIGINAL TRANSCRIPT:
${data.transcript}

---
Generated by Unscramble
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unscramble-thoughts.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!data) {
    return null;
  }

  return (
    <motion.div
      className="page results-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <header className="header">
        <Link to="/" className="logo">
          <img src={Title} alt="Unscramble Logo" className="logo-image" />
        </Link>
      </header>

      <main className="results-main">
        <motion.div
          className="results-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {data.detectedContext && (
            <div className="context-badge">
              Detected: {data.detectedContext}
            </div>
          )}

          <ResultsTabs
            brief={data.brief}
            detailed={data.detailed}
            transcript={data.transcript}
            onTabChange={setActiveTabContent}
          />

          <div className="results-actions">
            <button
              className="action-btn"
              onClick={handleCopy}
              aria-label="Copy to clipboard"
            >
              {copied ? <FiCheck /> : <FiCopy />}
              {copied ? 'Copied!' : 'Copy'}
            </button>

            <button
              className="action-btn"
              onClick={handleDownloadPDF}
              aria-label="Download as text"
            >
              <FiDownload />
              Download
            </button>

            <button
              className="action-btn primary"
              onClick={() => navigate('/record')}
              aria-label="Record again"
            >
              <FiMic />
              Ramble Again
            </button>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
};

export default Results;
